// ============================================================================
// dataTypes/liveStreamManagment/sourceRegistry/acquirers/SoundAcquirer.ts
// Acquiert un flux audio-only depuis un fichier (data: URL ou HTTP).
//
// Utilise WebAudio (AudioBufferSourceNode) pour la lecture, avec un
// MediaStreamAudioDestinationNode pour produire un MediaStream.
//
// Ce design permet au son d'être traité comme n'importe quelle source :
// il produit un stream qu'on peut mixer, mesurer, ou assigner à un slot.
// ============================================================================

import { Source, SourceAcquirer, SoundSourceConfig } from '../types';

interface SoundEntry {
  ctx:        AudioContext;
  sourceNode: AudioBufferSourceNode;
  gainNode:   GainNode;
  dest:       MediaStreamAudioDestinationNode;
  buffer:     AudioBuffer;
}

export class SoundAcquirer implements SourceAcquirer {

  private entries = new Map<string, SoundEntry>();

  /** Cache de buffers pré-chargés (clé = URL) */
  private bufferCache = new Map<string, AudioBuffer>();

  /** Callback optionnel — appelé quand le son se termine naturellement */
  onEnded?: (sourceId: string) => void;

  /**
   * Pré-charge un buffer audio dans le cache.
   * Appelé au montage pour que la lecture soit instantanée.
   */
  async preload(url: string): Promise<void> {
    if (this.bufferCache.has(url)) return;

    const ctx = new AudioContext();
    try {
      const buffer = await this.decodeUrl(url, ctx);
      this.bufferCache.set(url, buffer);
    } finally {
      ctx.close();
    }
  }

  async acquire(source: Source): Promise<MediaStream> {
    const config = source.config as SoundSourceConfig;

    // Nettoyage éventuel
    this.releaseEntry(source.id);

    const ctx  = new AudioContext();
    const dest = ctx.createMediaStreamDestination();

    // Charger ou récupérer le buffer
    let buffer = this.bufferCache.get(config.url);
    if (!buffer) {
      buffer = await this.decodeUrl(config.url, ctx);
      this.bufferCache.set(config.url, buffer);
    }

    // Créer la chaîne audio : source → gain → destination + speakers
    const sourceNode = ctx.createBufferSource();
    sourceNode.buffer = buffer;
    sourceNode.loop   = config.loop ?? false;

    const gainNode    = ctx.createGain();
    gainNode.gain.value = config.volume ?? 1;

    sourceNode.connect(gainNode);
    gainNode.connect(dest);
    gainNode.connect(ctx.destination); // monitoring local

    sourceNode.onended = () => {
      if (!(config.loop ?? false)) {
        this.onEnded?.(source.id);
      }
    };

    sourceNode.start();

    this.entries.set(source.id, { ctx, sourceNode, gainNode, dest, buffer });

    return dest.stream;
  }

  release(source: Source): void {
    this.releaseEntry(source.id);
  }

  /**
   * Modifie le volume d'un son en cours de lecture.
   */
  setVolume(sourceId: string, volume: number): void {
    const entry = this.entries.get(sourceId);
    if (entry) entry.gainNode.gain.value = volume;
  }

  private releaseEntry(sourceId: string): void {
    const entry = this.entries.get(sourceId);
    if (!entry) return;
    try {
      entry.sourceNode.onended = null;
      entry.sourceNode.stop();
      entry.sourceNode.disconnect();
      entry.gainNode.disconnect();
      entry.ctx.close();
    } catch { /* best-effort */ }
    this.entries.delete(sourceId);
  }

  private async decodeUrl(url: string, ctx: AudioContext): Promise<AudioBuffer> {
    let arrayBuf: ArrayBuffer;

    if (url.startsWith('data:')) {
      const base64 = url.split(',')[1];
      const binary = atob(base64);
      arrayBuf = new ArrayBuffer(binary.length);
      const view = new Uint8Array(arrayBuf);
      for (let i = 0; i < binary.length; i++) view[i] = binary.charCodeAt(i);
    } else {
      const res = await fetch(url);
      arrayBuf  = await res.arrayBuffer();
    }

    return ctx.decodeAudioData(arrayBuf);
  }

  /** Vide le cache de buffers pré-chargés */
  clearCache(): void {
    this.bufferCache.clear();
  }
}
